import "./stocktaking.scss";
import { ReactNode } from "react";
import { bottleStocktakingService, bottleTypeService, gasStocktakingService } from "../../api";
import { alertError, dataSources, enums, notifySuccess } from "../../base";
import { ContentBlock, FullPanel } from "../../components";
import { BottleStocktakingModel, BottleTypeModel, GasStocktakingItemCustomModel, GasStocktakingModel, GasStocktakingTypeItemCustomModel, StocktakingCondition } from "../../models";
import AuthorizedComponent from "../authorized-component";
import StocktakingToolbar from "./stocktaking-toolbar";
import { Button, NumberBox } from "devextreme-react";
import { ValueChangedEvent } from "devextreme/ui/number_box";

interface StocktakingPageState {
    bottleTypes: BottleTypeModel[];
    condition?: StocktakingCondition;
    gas?: GasStocktakingModel;
    bottle?: BottleStocktakingModel;
    gasMessage?: string;
    bottleMessage?: string;
}

export default class StocktakingPage extends AuthorizedComponent<any, StocktakingPageState> {
    objectName = "GasStocktaking";
    moduleName = "GasStocktaking";

    constructor(props: any) {
        super(props);

        this.state = {
            bottleTypes: []
        };

        this.search = this.search.bind(this);
        this.saveBottle = this.saveBottle.bind(this);
        this.saveGas = this.saveGas.bind(this);
    }

    componentDidMount(): void {
        super.componentDidMount();
        this.loadBottleTypes();
    }

    async loadBottleTypes() {
        const bottleTypes = await bottleTypeService.getRefs();
        this.setState({ bottleTypes: bottleTypes });
    }

    async search(condition: StocktakingCondition) {
        this.setState({ gas: undefined, bottle: undefined });

        const gas = await gasStocktakingService.get(condition);

        if (!gas.succeeded) {
            alertError(gas.message!);
            return;
        }

        const bottle = await bottleStocktakingService.get(condition);

        if (!bottle.succeeded) {
            alertError(bottle.message!);
            return;
        }

        this.setState({
            condition: condition,
            gas: gas.data,
            gasMessage: gas.message,
            bottle: bottle.data,
            bottleMessage: bottle.message
        })
    }

    setGasTypeItem(event: ValueChangedEvent, bottleTypeId?: number, operationType?: string) {
        if (!event.event) {
            return;
        }

        const bottles = event.value || 0;
        const items = this.state.gas!.items! as GasStocktakingItemCustomModel[];
        const item = items.find(i => i.bottleTypeId === bottleTypeId);
        const amount = Math.round(bottles * (item?.weight || 1) * 10) / 10;
        const typeItems = this.state.gas!.typeItems! as GasStocktakingTypeItemCustomModel[];
        const typeItem = typeItems.find(
            ti => ti.bottleTypeId === bottleTypeId && ti.operationType === operationType);

        var changedBottles = bottles - (typeItem?.bottles || 0);
        var changedAmount = amount - (typeItem?.amount || 0);
        var isOut = [enums.bottleOperations.stockOut, enums.bottleOperations.sendBack].indexOf(operationType || '') !== -1;

        if (isOut) {
            changedBottles = -changedBottles;
            changedAmount = -changedAmount;
        }

        if (typeItem) {
            typeItem.amount = amount;
            typeItem.bottles = bottles;
        } else {
            typeItems.push({
                bottleTypeId: bottleTypeId,
                operationType: operationType,
                direction: isOut ? enums.stockDirection.out : enums.stockDirection.in,
                amount: amount,
                bottles: bottles
            });
        }

        if (item) {
            item.expectBottles = (item.expectBottles || 0) + changedBottles;
            item.expectGas = Math.round(((item.expectGas || 0) + changedAmount) * 10) / 10;
        } else {
            items.push({
                bottleTypeId: bottleTypeId,
                expectGas: amount,
                expectBottles: bottles
            });
        }

        this.setState({ gas: this.state.gas });
    }

    setBottleTypeItem(event: any, bottleTypeId?: number, operationType?: string) {
        const value = Math.round(event.value || 0);
        const items = this.state.bottle!.items!;
        const item = items.find(i => i.bottleTypeId === bottleTypeId);
        const typeItems = this.state.bottle!.typeItems!;
        const typeItem = typeItems.find(
            ti => ti.bottleTypeId === bottleTypeId && ti.operationType === operationType);
        var changed = value - (typeItem?.amount || 0);
        var isOut = [enums.bottleOperations.stockOut, enums.bottleOperations.emptyBack, enums.bottleOperations.sendBack]
            .indexOf(operationType || '') !== -1;

        if (isOut) {
            changed = -changed;
        }

        if (typeItem) {
            typeItem.amount = value;
        } else {
            typeItems.push({
                bottleTypeId: bottleTypeId,
                operationType: operationType,
                direction: isOut ? enums.stockDirection.out : enums.stockDirection.in,
                amount: value
            });
        }

        if (item) {
            item.expectBottles = (item.expectBottles || 0) + changed;
        } else {
            items.push({
                bottleTypeId: bottleTypeId,
                expectBottles: value
            });
        }

        this.setState({ bottle: this.state.bottle });
    }

    setActualGas(event: ValueChangedEvent, bottleTypeId?: number) {
        const bottles = event.value || 0;
        const items = (this.state.gas?.items || []) as GasStocktakingItemCustomModel[];
        const item = items.find(i => i.bottleTypeId === bottleTypeId);
        const amount = Math.round(bottles * (item?.weight || 1) * 10) / 10;

        if (item) {
            item.actualBottles = bottles;
            item.actualGas = amount;
        } else {
            items.push({
                bottleTypeId: bottleTypeId,
                actualBottles: bottles,
                actualGas: amount
            });
        }

        this.setState({ gas: this.state.gas });
    }

    setActualBottle(event: any, bottleTypeId?: number) {
        const value = event.value || 0;
        const items = this.state.bottle?.items || [];
        const item = items.find(i => i.bottleTypeId === bottleTypeId);

        if (item) {
            item.actualBottles = value
        } else {
            items.push({
                bottleTypeId: bottleTypeId,
                actualBottles: value
            });
        }

        this.setState({ gas: this.state.gas });
    }

    async saveGas() {
        const result = await gasStocktakingService.save(this.state.gas!);

        if (result.succeeded) {
            this.refreshGas();
            notifySuccess("燃气库存保存成功");
        } else {
            alertError(result.message!);
        }
    }

    async refreshGas() {
        const gas = await gasStocktakingService.get(this.state.condition!);

        if (!gas.succeeded) {
            alertError(gas.message!);
            return;
        }
        
        this.setState({ gas: gas.data });
    }

    async saveBottle() {
        const result = await bottleStocktakingService.save(this.state.bottle!);

        if (result.succeeded) {
            this.refreshBottle();
            notifySuccess("气瓶库存保存成功");
        } else {
            alertError(result.message!);
        }
    }

    async refreshBottle() {
        const bottle = await bottleStocktakingService.get(this.state.condition!);

        if (!bottle.succeeded) {
            alertError(bottle.message!);
            return;
        }
        
        this.setState({ bottle: bottle.data });
    }

    render(): ReactNode {
        const { bottle, bottleTypes, gas, gasMessage, bottleMessage } = this.state;
        const stockOutType = this.state.condition?.outType;
        const typeText = dataSources.stockOutTypes.find(t => t.value === stockOutType)?.text;
        const canEdit = this.permissions[enums.stdActions.update];

        return (
            <ContentBlock caption={"库存盘点"}>
                <StocktakingToolbar onSearching={this.search} />
                <FullPanel>
                    <p className="text-warning">
                        注意！初次库存盘点前先设置初量，将从初量开始计算，后续则从上次盘点时间开始计算。
                        为避免混乱，系统不支持盘点以往的库存，只能盘点最新时间之后的库存，但已盘点过的数据可修改。
                    </p>
                    <div className="stocktaking-container">
                        <h4>燃气库存盘点</h4>
                        <div className="stocktaking-note">{gasMessage}</div>
                        <table>
                            <thead>
                                <tr>
                                    <th style={{width:80}}>商品名称</th>
                                    <th colSpan={2}>期初库存</th>
                                    <th colSpan={2}>重瓶调拨入库（+）</th>
                                    <th colSpan={2}>{typeText}（-）</th>
                                    <th colSpan={2}>重瓶退回供应站（+）</th>
                                    <th colSpan={2}>重瓶退回充装站（-）</th>
                                    <th colSpan={2}>应有库存</th>
                                    <th colSpan={2}>实际库存</th>
                                </tr>
                            </thead>
                            <tbody>
                                {gas && bottleTypes.map(t => {
                                    const item = gas.items?.find(i => i.bottleTypeId === t.id) as GasStocktakingItemCustomModel;
                                    const typeItems = (gas.typeItems?.filter(i => i.bottleTypeId === t.id) || []) as GasStocktakingTypeItemCustomModel[];
                                    const allot = typeItems.find(i => i.operationType === enums.bottleOperations.allot);
                                    const stockOut = typeItems.find(i => i.operationType === enums.bottleOperations.stockOut);
                                    const customerBack = typeItems.find(i => i.operationType === enums.bottleOperations.customerBack);
                                    const sendBack = typeItems.find(i => i.operationType === enums.bottleOperations.sendBack);

                                    return (
                                        <tr key={t.id}>
                                            <td>{t.name}液化气</td>
                                            <td>{item?.initBottles || 0} </td>
                                            <td>{item?.initGas || 0}kg</td>
                                            <td>
                                                <NumberBox value={allot?.bottles || 0}
                                                    onValueChanged={(event) => this.setGasTypeItem(event, t.id, enums.bottleOperations.allot)} />
                                            </td>
                                            <td>{allot?.amount || 0}kg</td>
                                            <td>
                                                <NumberBox value={stockOut?.bottles || 0}
                                                    onValueChanged={(event) => this.setGasTypeItem(event, t.id, enums.bottleOperations.stockOut)} />
                                            </td>
                                            <td>{stockOut?.amount || 0}kg</td>
                                            <td>
                                                <NumberBox value={customerBack?.bottles || 0}
                                                    onValueChanged={(event) => this.setGasTypeItem(event, t.id, enums.bottleOperations.customerBack)} />
                                            </td>
                                            <td>{customerBack?.amount || 0}kg</td>
                                            <td>
                                                <NumberBox value={sendBack?.bottles || 0}
                                                    onValueChanged={(event) => this.setGasTypeItem(event, t.id, enums.bottleOperations.sendBack)} />
                                            </td>
                                            <td>{sendBack?.amount || 0}kg</td>
                                            <td>{item?.expectBottles || 0}</td>
                                            <td>{item.expectGas || 0}kg</td>
                                            <td>
                                                <NumberBox value={item?.actualBottles || 0} onValueChanged={(event) => this.setActualGas(event, t.id)} />
                                            </td>
                                            <td>{item?.actualGas || 0}kg</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                        {gas && canEdit && (
                            <div className="stocktaking-button">
                                <Button text="确认和保存燃气库存" type="default" onClick={this.saveGas} />
                            </div>
                        )}
                        <h4>气瓶库存盘点</h4>
                        <div className="stocktaking-note">{bottleMessage}</div>
                        <table>
                            <thead>
                                <tr>
                                    <th>商品名称</th>
                                    <th>期初库存</th>
                                    <th>重瓶调拨入库（+）</th>
                                    <th>空瓶调拨出库（-）</th>
                                    <th>{typeText}（-）</th>
                                    <th>空瓶入库（+）</th>
                                    <th>重瓶退回供应站（+）</th>
                                    <th>重瓶退回充装站（-）</th>
                                    <th>应有库存</th>
                                    <th>实际库存</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bottle && bottleTypes.map(t => {
                                    const item = bottle.items?.find(i => i.bottleTypeId === t.id);
                                    const typeItems = bottle.typeItems?.filter(i => i.bottleTypeId === t.id) || [];

                                    return (
                                        <tr key={t.id}>
                                            <td>{t.name}瓶</td>
                                            <td>{item?.initBottles}</td>
                                            <td>
                                                <NumberBox value={typeItems.find(i => i.operationType === enums.bottleOperations.allot)?.amount}
                                                    onValueChanged={(event) => this.setBottleTypeItem(event, t.id, enums.bottleOperations.allot)} />
                                            </td>
                                            <td>
                                                <NumberBox value={typeItems.find(i => i.operationType === enums.bottleOperations.emptyBack)?.amount}
                                                    onValueChanged={(event) => this.setBottleTypeItem(event, t.id, enums.bottleOperations.emptyBack)} />
                                            </td>
                                            <td>
                                                <NumberBox value={typeItems.find(i => i.operationType === enums.bottleOperations.stockOut)?.amount}
                                                    onValueChanged={(event) => this.setBottleTypeItem(event, t.id, enums.bottleOperations.stockOut)} />
                                            </td>
                                            <td>
                                                <NumberBox value={typeItems.find(i => i.operationType === enums.bottleOperations.emptyIn)?.amount}
                                                    onValueChanged={(event) => this.setBottleTypeItem(event, t.id, enums.bottleOperations.emptyIn)} />
                                            </td>
                                            <td>
                                                <NumberBox value={typeItems.find(i => i.operationType === enums.bottleOperations.customerBack)?.amount}
                                                    onValueChanged={(event) => this.setBottleTypeItem(event, t.id, enums.bottleOperations.customerBack)} />
                                            </td>
                                            <td>
                                                <NumberBox value={typeItems.find(i => i.operationType === enums.bottleOperations.sendBack)?.amount}
                                                    onValueChanged={(event) => this.setBottleTypeItem(event, t.id, enums.bottleOperations.sendBack)} />
                                            </td>
                                            <td>{item?.expectBottles}</td>
                                            <td>
                                                <NumberBox value={item?.actualBottles} onValueChanged={(event) => this.setActualBottle(event, t.id)} />
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                        {bottle && canEdit && (
                            <div className="stocktaking-button">
                                <Button text="确认和保存气瓶库存" type="default" onClick={this.saveBottle} />
                            </div>
                        )}
                    </div>
                </FullPanel>
            </ContentBlock>
        )
    }
}